import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import styles from '../styles/SignatureModal.module.scss';
import { useViewportHeightVar } from '../../../shared/hooks/useViewportHeightVar';

// Mobile detection utility
const isMobileDevice = (): boolean => {
  return (
    window.innerWidth <= 768 ||
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};

interface SignatureModalProps {
  fieldType: 'signature' | 'initials';
  existingSignature?: string;
  existingMethod?: 'draw' | 'type' | 'upload';
  existingText?: string;
  existingFont?: string;
  onComplete: (
    signatureData: string,
    method: 'draw' | 'type' | 'upload',
    metadata?: { text?: string; font?: string }
  ) => void;
  onClose: () => void;
}

type TabType = 'draw' | 'type' | 'upload';

// Font styles for signatures
const fontStyles = [
  { id: 'allura', name: 'Allura', fontFamily: '"Allura", "Brush Script MT", cursive' },
  { id: 'dancing', name: 'Dancing Script', fontFamily: '"Dancing Script", "Comic Sans MS", cursive' },
  { id: 'greatvibes', name: 'Great Vibes', fontFamily: '"Great Vibes", "Lucida Handwriting", cursive' },
  { id: 'pacifico', name: 'Pacifico', fontFamily: '"Pacifico", "Bradley Hand", cursive' },
  { id: 'sacramento', name: 'Sacramento', fontFamily: '"Sacramento", "Segoe Script", cursive' },
  { id: 'caveat', name: 'Caveat', fontFamily: '"Caveat", "Marker Felt", cursive' },
];

// iOS detection utility
const isIOSDevice = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// Fullscreen helpers
const enterFullscreen = async (element: HTMLElement) => {
  // Skip fullscreen on iOS - not supported
  if (isIOSDevice()) return;

  try {
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    }
  } catch (err) {
    // Fullscreen not supported or user denied - that's ok
  }
};

const exitFullscreen = async () => {
  // Skip fullscreen on iOS - not supported
  if (isIOSDevice()) return;

  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    }
  } catch (err) {
    // Ignore errors on exit
  }
};

export const SignatureModal: React.FC<SignatureModalProps> = ({
  fieldType,
  existingSignature,
  existingMethod,
  existingText,
  existingFont,
  onComplete,
  onClose,
}) => {
  // Use viewport height fix for iOS Safari
  useViewportHeightVar();

  // Mobile detection
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [showRotateHint, setShowRotateHint] = useState(() => {
    // Check if user already dismissed the hint in this session
    try {
      const dismissed = sessionStorage.getItem('rotate_hint_dismissed');
      return dismissed !== 'true'; // Show if NOT dismissed
    } catch {
      return true; // Default to showing if storage unavailable
    }
  });
  const modalRootRef = useRef<HTMLDivElement>(null);
  
  // Use existing method if available, otherwise default to draw
  const [activeTab, setActiveTab] = useState<TabType>(existingMethod || 'draw');
  const [typedText, setTypedText] = useState(existingText || '');
  const [selectedFont, setSelectedFont] = useState(existingFont || fontStyles[0].id);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasLoadedSignature, setHasLoadedSignature] = useState(false);
  const [hasDrawnContent, setHasDrawnContent] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(
    existingMethod === 'upload' ? existingSignature || null : null
  );

  const title = fieldType === 'signature' ? 'Signature' : 'Initials';
  const placeholder = fieldType === 'signature' ? 'Type your full name' : 'Type your initials';

  // Reset hasLoadedSignature when signature changes (opening different field)
  useEffect(() => {
    setHasLoadedSignature(false);
    setHasDrawnContent(false);
  }, [existingSignature]);

  // Enter fullscreen on mobile when modal opens
  useEffect(() => {
    if (isMobile && modalRootRef.current) {
      enterFullscreen(modalRootRef.current);
    }

    return () => {
      if (isMobile) {
        exitFullscreen();
      }
    };
  }, [isMobile]);

  // If we have existing typed text and we're showing the type tab, generate preview
  useEffect(() => {
    if (existingMethod === 'type' && existingText && existingFont && activeTab === 'type') {
      // The typed text and font are already loaded via initial state
      // No additional action needed as the UI will render with the values
    }
  }, [existingMethod, existingText, existingFont, activeTab]);

  // Detect orientation
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  
  // Handle resize events for mobile orientation changes
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    
    const handleResize = () => {
      // Clear any pending resize
      if (resizeTimeout) clearTimeout(resizeTimeout);
      
      // Debounce rapid resize events
      resizeTimeout = setTimeout(() => {
        const mobile = isMobileDevice();
        const portrait = window.innerHeight > window.innerWidth;
        
        setIsMobile(mobile);
        setIsPortrait(portrait);

        // Show rotate hint only on mobile in portrait mode for draw tab, and only if not already dismissed
        if (mobile && portrait && activeTab === 'draw') {
          try {
            const dismissed = sessionStorage.getItem('rotate_hint_dismissed');
            if (dismissed !== 'true') {
              setShowRotateHint(true);
            }
          } catch {
            setShowRotateHint(true); // Default to showing if storage unavailable
          }
        } else {
          setShowRotateHint(false);
        }
      }, 150);
    };

    const handleOrientationChange = () => {
      // Orientation change needs longer delay to ensure layout is settled
      setTimeout(handleResize, 300);
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [activeTab]);

  // Mobile-optimized drawing functionality
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || activeTab !== 'draw') return;

    // Get context with willReadFrequently for better performance
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Mobile-optimized canvas sizing with device pixel ratio
    const setupCanvas = (retryCount = 0) => {
      // Force layout recalculation before getting dimensions
      void canvas.offsetHeight; // Trigger reflow
      
      const rect = canvas.getBoundingClientRect();
      const devicePixelRatio = window.devicePixelRatio || 1;
      
      // If dimensions are 0 and we haven't retried too many times, try again after layout settles
      if ((rect.width === 0 || rect.height === 0) && retryCount < 3) {
        setTimeout(() => setupCanvas(retryCount + 1), 50);
        return;
      }
      
      // Set actual canvas size in memory (scaled up for sharp rendering)
      canvas.width = Math.max(rect.width, 100) * devicePixelRatio; // Minimum 100px width
      canvas.height = Math.max(rect.height, 100) * devicePixelRatio; // Minimum 100px height
      
      // Scale down CSS size to maintain display size
      canvas.style.width = Math.max(rect.width, 100) + 'px';
      canvas.style.height = Math.max(rect.height, 100) + 'px';
      
      // Reset transform to avoid cumulative scaling
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      // Scale the drawing context so everything draws at correct size
      ctx.scale(devicePixelRatio, devicePixelRatio);
      
      // Mobile-optimized drawing styles
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = isMobile ? 3 : 2; // Thicker line for mobile
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.imageSmoothingEnabled = true;
      
      // Draw baseline guide after setup (pass rect for correct dimensions)
      drawBaseline(rect);
    };

    // Draw dotted baseline guide (like DocuSign)
    const drawBaseline = (rect: DOMRect) => {
      // Save current context state
      ctx.save();
      
      // Set dotted line style
      ctx.strokeStyle = '#d0d0d0'; // Light gray
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]); // 3px dash, 3px gap
      
      // Draw line at about 75% height (standard signature baseline position)
      // No need to divide by devicePixelRatio - context is already scaled
      const baselineY = rect.height * 0.75;
      ctx.beginPath();
      ctx.moveTo(20, baselineY); // Start with padding from left
      ctx.lineTo(rect.width - 20, baselineY); // End with padding from right
      ctx.stroke();
      
      // Restore context state (removes dash pattern for actual drawing)
      ctx.restore();
    };

    setupCanvas();

    let drawing = false;
    let lastX = 0;
    let lastY = 0;
    let currentSignatureData: string | null = null;

    // Save current signature before resize
    const saveCurrentSignature = () => {
      if (hasDrawnContent) {
        currentSignatureData = canvas.toDataURL('image/png');
      }
    };

    // Restore signature after resize
    const restoreSignature = () => {
      const rect = canvas.getBoundingClientRect();
      if (currentSignatureData) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));
          drawBaseline(rect); // Redraw baseline first with rect
          ctx.drawImage(img, 0, 0);
        };
        img.src = currentSignatureData;
      } else {
        // If no signature to restore, just redraw the baseline
        drawBaseline(rect);
      }
    };

    const getCoordinates = (e: MouseEvent | TouchEvent): { x: number; y: number } => {
      // ALWAYS get a fresh bounding rect - don't cache it
      // This is critical for Safari iOS where the rect can change during interactions
      const rect = canvas.getBoundingClientRect();

      // rect.width reflects the rendered size; canvas.style.width is unset until the browser applies layout.
      // The canvas CSS might be stale, but rect.width is always current
      const scaleX = canvas.width / (rect.width * devicePixelRatio);
      const scaleY = canvas.height / (rect.height * devicePixelRatio);

      if (e instanceof MouseEvent) {
        return {
          x: (e.clientX - rect.left) * scaleX,
          y: (e.clientY - rect.top) * scaleY
        };
      } else if (e instanceof TouchEvent && e.touches.length > 0) {
        // Prevent default touch behavior to avoid scrolling
        e.preventDefault();

        const touch = e.touches[0];

        // Calculate coordinates with scale correction
        const x = (touch.clientX - rect.left) * scaleX;
        const y = (touch.clientY - rect.top) * scaleY;

        return { x, y };
      }
      return { x: 0, y: 0 };
    };

    const draw = (e: MouseEvent | TouchEvent) => {
      if (!drawing) return;

      const { x: currentX, y: currentY } = getCoordinates(e);

      // Draw using CSS pixel coordinates
      // The context is already scaled by devicePixelRatio, so these coordinates
      // will be automatically multiplied to match the canvas's internal resolution
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(currentX, currentY);
      ctx.stroke();

      lastX = currentX;
      lastY = currentY;
    };

    const stopDrawing = () => {
      drawing = false;
      setIsDrawing(false);
      
      // Remove document-level listeners when drawing stops
      document.removeEventListener('mousemove', draw);
      document.removeEventListener('mouseup', stopDrawing);
      document.removeEventListener('touchmove', draw, { passive: false } as any);
      document.removeEventListener('touchend', stopDrawing);
    };

    const startDrawing = (e: MouseEvent | TouchEvent) => {
      drawing = true;
      const { x, y } = getCoordinates(e);
      
      lastX = x;
      lastY = y;

      if (e instanceof MouseEvent) {
        // Add document-level listeners for mouse events
        document.addEventListener('mousemove', draw);
        document.addEventListener('mouseup', stopDrawing);
      } else if (e instanceof TouchEvent) {
        // Prevent touch scrolling and zoom
        e.preventDefault();
        
        // Add document-level listeners for touch events with passive: false for preventDefault
        document.addEventListener('touchmove', draw, { passive: false });
        document.addEventListener('touchend', stopDrawing);
      }

      setIsDrawing(true);
      setHasDrawnContent(true);
    };

    // Handle canvas resize for orientation changes
    const handleResize = () => {
      saveCurrentSignature();
      
      // Use longer delay for mobile orientation changes to ensure layout is settled
      const delay = isMobile ? 300 : 100;
      setTimeout(() => {
        setupCanvas(0); // Start fresh retry count
        setTimeout(() => {
          restoreSignature();
        }, 50); // Small delay between setup and restore
      }, delay);
    };

    // Event listeners
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    
    // Handle resize for mobile orientation changes
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      // Clean up canvas listeners
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('touchstart', startDrawing);

      // Clean up document listeners
      document.removeEventListener('mousemove', draw);
      document.removeEventListener('mouseup', stopDrawing);
      document.removeEventListener('touchmove', draw);
      document.removeEventListener('touchend', stopDrawing);

      // Clean up resize listeners
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [activeTab, existingSignature, existingMethod, hasLoadedSignature]);

  // Lock body scroll while drawing to prevent accidental page scroll on iOS
  useEffect(() => {
    if (isDrawing) {
      // Save current scroll position
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      // Lock body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `-${scrollX}px`;
      document.body.style.width = '100%';

      return () => {
        // Restore body
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.width = '';

        // Restore scroll position
        window.scrollTo(scrollX, scrollY);
      };
    }
  }, [isDrawing]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Get rect for baseline
    const rect = canvas.getBoundingClientRect();
    const devicePixelRatio = window.devicePixelRatio || 1;

    // Clear with device pixel ratio consideration
    ctx.clearRect(0, 0, canvas.width / devicePixelRatio, canvas.height / devicePixelRatio);

    // Redraw the baseline guide after clearing
    ctx.save();
    ctx.strokeStyle = '#d0d0d0'; // Light gray
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]); // 3px dash, 3px gap
    // No division by devicePixelRatio - context is already scaled
    const baselineY = rect.height * 0.75;
    ctx.beginPath();
    ctx.moveTo(20, baselineY);
    ctx.lineTo(rect.width - 20, baselineY);
    ctx.stroke();
    ctx.restore();

    setHasLoadedSignature(false); // Reset so signature can be reloaded if needed
    setHasDrawnContent(false); // Reset drawn content flag
  };

  const generateTypedSignature = (fontId?: string) => {
    // Get font style
    const fontToUse = fontId || selectedFont;
    const fontStyle = fontStyles.find((f) => f.id === fontToUse) || fontStyles[0];
    const fontSize = fieldType === 'initials' ? 48 : 60;
    const font = `${fontSize}px ${fontStyle.fontFamily}`;

    // Step 1: Draw on an oversized canvas to ensure nothing is clipped
    const bigCanvas = document.createElement('canvas');
    bigCanvas.width = 800;
    bigCanvas.height = fontSize * 3; // Generous vertical space for any font
    const bigCtx = bigCanvas.getContext('2d');
    if (!bigCtx) return '';

    bigCtx.font = font;
    bigCtx.fillStyle = '#000000';
    bigCtx.textAlign = 'left';
    bigCtx.textBaseline = 'middle';
    bigCtx.fillText(typedText, 20, bigCanvas.height / 2);

    // Step 2: Crop to bounding box of non-transparent pixels
    const imageData = bigCtx.getImageData(0, 0, bigCanvas.width, bigCanvas.height);
    const data = imageData.data;
    const w = bigCanvas.width;
    const h = bigCanvas.height;
    let minX = w, minY = h, maxX = 0, maxY = 0;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (data[(y * w + x) * 4 + 3] > 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX) return ''; // No content drawn

    const pad = 2;
    const cropX = Math.max(0, minX - pad);
    const cropY = Math.max(0, minY - pad);
    const cropW = Math.min(w, maxX + pad + 1) - cropX;
    const cropH = Math.min(h, maxY + pad + 1) - cropY;

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = cropW;
    croppedCanvas.height = cropH;
    const croppedCtx = croppedCanvas.getContext('2d');
    if (!croppedCtx) return '';

    croppedCtx.drawImage(bigCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    return croppedCanvas.toDataURL('image/png');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleApply = () => {
    let signatureData = '';
    let metadata: { text?: string; font?: string } | undefined;

    switch (activeTab) {
      case 'draw':
        const canvas = canvasRef.current;
        if (canvas) {
          // Get the current canvas image data
          const imageData = canvas.getContext('2d', { willReadFrequently: true })?.getImageData(0, 0, canvas.width, canvas.height);

          if (imageData) {
            // Step 1: Remove baseline guide pixels (light gray #d0d0d0)
            const data = imageData.data;
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];
              const a = data[i + 3];

              if (r >= 200 && r <= 216 && g >= 200 && g <= 216 && b >= 200 && b <= 216 && a > 0) {
                data[i + 3] = 0; // Make transparent
              }
            }

            // Step 2: Find bounding box of non-transparent pixels
            const w = canvas.width;
            const h = canvas.height;
            let minX = w, minY = h, maxX = 0, maxY = 0;
            let hasContent = false;

            for (let y = 0; y < h; y++) {
              for (let x = 0; x < w; x++) {
                const alpha = data[(y * w + x) * 4 + 3];
                if (alpha > 0) {
                  if (x < minX) minX = x;
                  if (x > maxX) maxX = x;
                  if (y < minY) minY = y;
                  if (y > maxY) maxY = y;
                  hasContent = true;
                }
              }
            }

            if (hasContent) {
              // Step 3: Crop to bounding box with small padding
              const cropPad = 4;
              const cropX = Math.max(0, minX - cropPad);
              const cropY = Math.max(0, minY - cropPad);
              const cropW = Math.min(w, maxX + cropPad + 1) - cropX;
              const cropH = Math.min(h, maxY + cropPad + 1) - cropY;

              // Put cleaned data on a temp canvas to extract the crop region
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = w;
              tempCanvas.height = h;
              const tempCtx = tempCanvas.getContext('2d');
              if (tempCtx) {
                tempCtx.putImageData(imageData, 0, 0);

                // Create final cropped canvas
                const croppedCanvas = document.createElement('canvas');
                croppedCanvas.width = cropW;
                croppedCanvas.height = cropH;
                const croppedCtx = croppedCanvas.getContext('2d');
                if (croppedCtx) {
                  croppedCtx.drawImage(tempCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
                  signatureData = croppedCanvas.toDataURL('image/png');
                }
              }
            }
          }
        }
        break;
      case 'type':
        if (typedText) {
          signatureData = generateTypedSignature();
          metadata = { text: typedText, font: selectedFont };
        }
        break;
      case 'upload':
        if (uploadedImage) {
          signatureData = uploadedImage;
        }
        break;
    }

    if (signatureData) {
      if (isMobile) {
        exitFullscreen();
      }
      onComplete(signatureData, activeTab, metadata);
    }
  };

  const handleClose = () => {
    if (isMobile) {
      exitFullscreen();
    }
    onClose();
  };

  const canApply = () => {
    switch (activeTab) {
      case 'draw':
        return hasDrawnContent || (existingMethod === 'draw' && existingSignature && hasLoadedSignature);
      case 'type':
        return typedText.length > 0;
      case 'upload':
        return uploadedImage !== null;
      default:
        return false;
    }
  };

  const isCanvasEmpty = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return true;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return !imageData.data.some((channel) => channel !== 0);
  };

  return (
    <div className={`${styles.modalOverlay} ${isMobile ? styles.mobileOverlay : ''}`} ref={modalRootRef}>
      <div className={`${styles.modal} ${isMobile ? styles.mobileModal : ''}`}>
        {/* DocuSign-style Header: Cancel - Title - Apply */}
        <div className={`${styles.header} ${isMobile ? styles.mobileHeader : ''}`}>
          <Button variant="secondary" onClick={handleClose} className={styles.cancelButton}>
            Cancel
          </Button>
          <h2 className={styles.title}>{title}</h2>
          <Button 
            variant="primary" 
            onClick={handleApply} 
            disabled={!canApply()}
            className={styles.applyButton}
          >
            Apply
          </Button>
        </div>

        {/* Main Canvas Area - Side-by-side in landscape on mobile */}
        <div className={`${styles.canvasArea} ${isMobile ? styles.mobileCanvasArea : ''} ${isMobile && !isPortrait ? styles.mobileLandscapeCanvasArea : ''}`}>
          {/* Left side controls for mobile landscape */}
          {isMobile && !isPortrait && (
            <div className={styles.landscapeSideControls}>
              <div className={styles.modeButtons}>
                <button
                  className={`${styles.modeButton} ${activeTab === 'draw' ? styles.active : ''}`}
                  onClick={() => {
                    setActiveTab('draw');
                  }}
                >
                  <span className={styles.modeIcon}>✍️</span>
                  <span className={styles.modeText}>Draw</span>
                </button>
                <button
                  className={`${styles.modeButton} ${activeTab === 'type' ? styles.active : ''}`}
                  onClick={() => {
                    setActiveTab('type');
                    if (!existingText) {
                      setUploadedImage(null);
                    }
                  }}
                >
                  <span className={styles.modeIcon}>Tt</span>
                  <span className={styles.modeText}>Type</span>
                </button>
              </div>
              <button 
                className={styles.clearButton} 
                onClick={activeTab === 'draw' ? clearCanvas : activeTab === 'type' ? () => setTypedText('') : () => setUploadedImage(null)}
                disabled={activeTab === 'draw' ? !hasDrawnContent : activeTab === 'type' ? !typedText : !uploadedImage}
              >
                <span className={styles.clearIcon}>✕</span>
                <span className={styles.clearText}>Clear</span>
              </button>
            </div>
          )}
          
          {/* Canvas wrapper for landscape */}
          <div className={isMobile && !isPortrait ? styles.landscapeCanvasWrapper : ''}>
            {activeTab === 'draw' && (
              <div className={`${styles.canvasContainer} ${isMobile && isPortrait ? styles.mobilePortraitContainer : ''} ${isMobile && !isPortrait ? styles.mobileLandscapeContainer : ''}`}>
                <canvas 
                  ref={canvasRef} 
                  className={`${styles.canvas} ${isMobile ? styles.mobileCanvas : ''} ${isMobile && isPortrait ? styles.mobilePortraitCanvas : ''} ${isMobile && !isPortrait ? styles.mobileLandscapeCanvas : ''}`}
                  style={{ 
                    cursor: 'crosshair',
                    // Force aspect ratio for mobile to ensure horizontal rectangle
                    ...(isMobile && isPortrait ? { aspectRatio: '2.5 / 1' } : {})
                  }} 
                />
                {showRotateHint && (
                  <div
                    className={styles.rotateHintOverlay}
                    onClick={() => {
                      setShowRotateHint(false);
                      // Remember dismissal for this session
                      try {
                        sessionStorage.setItem('rotate_hint_dismissed', 'true');
                      } catch {
                        // Ignore storage errors
                      }
                    }}
                  >
                    <div className={styles.rotateHintContent}>
                      <div className={styles.rotateIcon}>📱 ↻</div>
                      <div className={styles.rotateText}>
                        Tip: Rotate for larger signing area
                      </div>
                      <div className={styles.dismissText}>Tap to continue</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'type' && (
              <div className={`${styles.typeTab} ${isMobile ? styles.mobileTypeTab : ''}`}>
                <input
                  type="text"
                  placeholder={placeholder}
                  value={typedText}
                  onChange={(e) => setTypedText(e.target.value)}
                  className={`${styles.textInput} ${isMobile ? styles.mobileTextInput : ''}`}
                  autoFocus
                />
                {typedText && (
                  <div className={`${styles.fontGrid} ${isMobile ? styles.mobileFontGrid : ''}`}>
                    {fontStyles.map((font) => (
                      <div
                        key={font.id}
                        className={`${styles.fontCard} ${selectedFont === font.id ? styles.selected : ''}`}
                        onClick={() => setSelectedFont(font.id)}
                      >
                        <div className={styles.fontPreview} style={{ fontFamily: font.fontFamily, fontSize: isMobile ? '20px' : '24px' }}>
                          {typedText}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!typedText && (
                  <div className={styles.typeHint}>
                    Type your {fieldType === 'signature' ? 'name' : 'initials'} above to see font styles
                  </div>
                )}
              </div>
            )}

            {activeTab === 'upload' && (
              <div className={`${styles.uploadTab} ${isMobile ? styles.mobileUploadTab : ''}`}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                {uploadedImage ? (
                  <div className={styles.uploadedImage}>
                    <img src={uploadedImage} alt="Uploaded signature" />
                    <Button variant="secondary" size="sm" onClick={() => setUploadedImage(null)}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className={styles.uploadArea} onClick={() => fileInputRef.current?.click()}>
                    <p>Click to upload an image of your {fieldType}</p>
                    <p className={styles.uploadHint}>PNG or JPG</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* DocuSign-style Bottom Controls: Draw/Type toggles - Clear */}
        {/* Only show bottom controls for desktop or mobile portrait mode */}
        {(!isMobile || isPortrait) && (
          <div className={`${styles.controls} ${isMobile ? styles.mobileControls : ''}`}>
            <div className={styles.modeButtons}>
              <button
                className={`${styles.modeButton} ${activeTab === 'draw' ? styles.active : ''}`}
                onClick={() => {
                  setActiveTab('draw');
                  // Don't reset hasLoadedSignature here - let useEffect handle it
                }}
              >
                <span className={styles.modeIcon}>✍️</span>
                <span className={styles.modeText}>Draw</span>
              </button>
              <button
                className={`${styles.modeButton} ${activeTab === 'type' ? styles.active : ''}`}
                onClick={() => {
                  setActiveTab('type');
                  // If we don't have existing typed text, clear any uploaded image
                  if (!existingText) {
                    setUploadedImage(null);
                  }
                }}
              >
                <span className={styles.modeIcon}>Tt</span>
                <span className={styles.modeText}>Type</span>
              </button>
              {!isMobile && (
                <button
                  className={`${styles.modeButton} ${activeTab === 'upload' ? styles.active : ''}`}
                  onClick={() => setActiveTab('upload')}
                >
                  <span className={styles.modeIcon}>📁</span>
                  <span className={styles.modeText}>Upload</span>
                </button>
              )}
            </div>
            
            <button 
              className={styles.clearButton} 
              onClick={activeTab === 'draw' ? clearCanvas : activeTab === 'type' ? () => setTypedText('') : () => setUploadedImage(null)}
              disabled={activeTab === 'draw' ? !hasDrawnContent : activeTab === 'type' ? !typedText : !uploadedImage}
            >
              <span className={styles.clearIcon}>✕</span>
              <span className={styles.clearText}>Clear</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
